import { type AdChatPrompt, titleOf } from "@hooks/useAdChatPrompts";

/**
 * `encodeURIComponent` + **额外转义 `(` 与 `)`**。
 *
 * 为什么要多这一步：`encodeURIComponent` 不转义括号，而协议行本身是个 markdown 链接
 * `[label](...)` —— referenceSource 里出现的 `)` 会把链接**提前闭合**，整条协议解析失败。
 * 对齐 `screens/briefing/themeRankAskEd.ts:44-46` 与 `screens/report/buildReportAskEdQuestion.ts`。
 */
export function encodeAdChatReferenceSource(raw: string): string {
  return encodeURIComponent(raw).replace(/\(/g, "%28").replace(/\)/g, "%29");
}

/**
 * 拼装发给 Ed 的第一条消息。
 *
 * - `referenceType` 为空 → 直接返回问题原文（纯文本发送）
 * - 非空 → `[](referenceType=X&referenceSource=Y)\n{问题原文}`
 * - `referenceSource` 为空 → 协议行只带 `referenceType`
 *
 * 方括号**留空但不能省**：perplexica `parsePrimaryMessageReference` 的正则是
 * `^\[([^\]]*)\]\(([^)]+)\)$`，没有 `[]` 就不认协议行、整条当纯文本。留空则气泡只显示问题、
 * 不出引用条；会话标题取正文（`buildChatTitleFromContent` 正文优先）。
 */
export function buildAdChatMessage(
  prompt: AdChatPrompt,
  locale: string,
): string {
  const text = titleOf(prompt, locale);
  const referenceType = prompt.referenceType?.trim();
  if (!referenceType) {
    return text;
  }
  const referenceSource = prompt.referenceSource?.trim();
  const source = referenceSource
    ? `&referenceSource=${encodeAdChatReferenceSource(referenceSource)}`
    : "";
  return `[](referenceType=${referenceType}${source})\n${text}`;
}
